import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import type { ITopData } from '../models/ITopData';
import type { ITotalTrackedTime } from '../models/ITotalTrackedTime';
import type { IEmployeeTrackedTime, ITrackedTime } from '../models/IEmployeeTrackedTime';
import type { ISummaryClientOption } from '../models/ISummaryClientOption';
import type { ISummaryCurrencyOption } from '../models/ISummaryCurrencyOption';
import type { ISummaryDualCurrencyResponse } from '../models/ISummaryDualCurrency';
import { toIsoWithDateOffset } from '../utils/dateUtils';
import type { IMonthlyClientFinanceSummary } from '../models/IMonthlyClientFinance';

const SUMMARY_API = '/umbraco/api/Summary';

type ApiResponse<T> = {
  Code: number;
  data: T;
  message?: string;
};

export interface DetailRoute {
  url: string;
  detailFormKey: string;
}

type IDateRangeQueryParams = {
  start: Date;
  end: Date;
  useLocalTime: boolean;
  clientId?: number;
};

type IDateRangeWithDashboardQueryParams = IDateRangeQueryParams & {
  dashboardId: number;
};

type IDateRangeModeDashboardParams = {
  dashboardId: number;
  dateRangeMode: string;
};

type IMonthlyDateRangeQueryParams = {
  dateRanges: { startDate: Date; endDate: Date }[];
  useLocalTime: boolean;
  clientId?: number;
};

type IMonthlyClientFinanceParams = { monthStart: Date; dashboardId: number; clientId?: number };

function createTransformResponse<T>(errorMessage?: string) {
  return (response: ApiResponse<T>) => {
    if (response.Code === 1) return response.data;
    throw new Error(errorMessage || response.message || 'Failed to load data');
  };
}

function createTransformResponseNullable<T>() {
  return (response: ApiResponse<T | null>) => {
    if (response.Code === 1) return response.data;
    return null;
  };
}

export const summaryApi = createApi({
  reducerPath: 'summaryApi',
  baseQuery: baseQuery,
  tagTypes: ['Summary'],
  endpoints: (builder) => ({
    getDashboardId: builder.query<number | null, string>({
      query: (pageUrl) => ({
        url: `${SUMMARY_API}/GetDashboardId`,
        params: { pageUrl },
      }),
      transformResponse: createTransformResponseNullable<number>(),
      providesTags: [{ type: 'Summary', id: 'dashboard-id' }],
    }),

    getMonthlyClientFinanceEnabled: builder.query<boolean, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetMonthlyClientFinanceEnabled`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<boolean>(
        'Failed to load monthly finance settings'
      ),
    }),

    getMonthlyClientFinanceSummary: builder.query<
      IMonthlyClientFinanceSummary,
      IMonthlyClientFinanceParams
    >({
      query: ({ monthStart, dashboardId, clientId }) => ({
        url: `${SUMMARY_API}/GetMonthlyClientFinanceSummary`,
        params: { monthStart: toIsoWithDateOffset(monthStart, false), dashboardId, clientId },
      }),
      transformResponse: createTransformResponse<IMonthlyClientFinanceSummary>(
        'Failed to load monthly client finance summary'
      ),
      providesTags: [{ type: 'Summary', id: 'monthly-client-finance' }],
    }),

    getRecordsNumberForTopEmployees: builder.query<number, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetRecordsNumberForTopEmployees`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<number>(
        'Failed to load records number for Top Employees'
      ),
      providesTags: [{ type: 'Summary', id: 'records-number-top-employees' }],
    }),

    getRecordsNumberForTopProjects: builder.query<number, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetRecordsNumberForTopProjects`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<number>(
        'Failed to load records number for Top Projects'
      ),
      providesTags: [{ type: 'Summary', id: 'records-number-top-projects' }],
    }),

    getTopProjects: builder.query<ISummaryDualCurrencyResponse<ITopData>, IDateRangeQueryParams>({
      query: ({ start, end, useLocalTime, clientId }) => ({
        url: `${SUMMARY_API}/GetTopProjects`,
        params: {
          start: toIsoWithDateOffset(start, useLocalTime),
          end: toIsoWithDateOffset(end, useLocalTime),
          clientId,
        },
      }),
      transformResponse: createTransformResponse<ISummaryDualCurrencyResponse<ITopData>>(
        'Failed to load Top Projects'
      ),
      providesTags: [{ type: 'Summary', id: 'top-projects' }],
      keepUnusedDataFor: 300,
    }),

    getTopEmployees: builder.query<ISummaryDualCurrencyResponse<ITopData>, IDateRangeQueryParams>({
      query: ({ start, end, useLocalTime, clientId }) => ({
        url: `${SUMMARY_API}/GetTopEmployees`,
        params: {
          start: toIsoWithDateOffset(start, useLocalTime),
          end: toIsoWithDateOffset(end, useLocalTime),
          clientId,
        },
      }),
      transformResponse: createTransformResponse<ISummaryDualCurrencyResponse<ITopData>>(
        'Failed to load Top Employees'
      ),
      providesTags: [{ type: 'Summary', id: 'top-employees' }],
      keepUnusedDataFor: 300,
    }),

    getTotalTrackedTime: builder.query<ITotalTrackedTime, IDateRangeQueryParams>({
      query: ({ start, end, useLocalTime, clientId }) => ({
        url: `${SUMMARY_API}/GetTotalTrackedTime`,
        params: {
          start: toIsoWithDateOffset(start, useLocalTime),
          end: toIsoWithDateOffset(end, useLocalTime),
          clientId,
        },
      }),
      transformResponse: createTransformResponse<ITotalTrackedTime>(
        'Failed to load Total Tracked Time'
      ),
      providesTags: [{ type: 'Summary', id: 'total-tracked-time' }],
    }),

    getEmployeeTrackedTime: builder.query<
      ISummaryDualCurrencyResponse<IEmployeeTrackedTime>,
      IDateRangeWithDashboardQueryParams
    >({
      query: ({ start, end, useLocalTime, dashboardId, clientId }) => ({
        url: `${SUMMARY_API}/GetEmployeesTrackedTimePerDay`,
        params: {
          start: toIsoWithDateOffset(start, useLocalTime),
          end: toIsoWithDateOffset(end, useLocalTime),
          dashboardId,
          clientId,
        },
      }),
      transformResponse: createTransformResponse<
        ISummaryDualCurrencyResponse<IEmployeeTrackedTime>
      >('Failed to load Employee Tracked Time'),
      providesTags: [{ type: 'Summary', id: 'employee-tracked-time' }],
      keepUnusedDataFor: 60,
    }),

    getEmployeeWorkLogLink: builder.query<string, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetEmployeeWorkLogLink`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<string>('Failed to load Employee Work Log link'),
      providesTags: [{ type: 'Summary', id: 'employee-worklog-link' }],
    }),

    getProjectWorkLogLink: builder.query<string, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetProjectWorkLogLink`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<string>('Failed to load Project Work Log link'),
      providesTags: [{ type: 'Summary', id: 'project-worklog-link' }],
    }),

    getHourlyTableWorkLogLink: builder.query<string, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetHourlyTableWorkLogLink`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<string>(
        'Failed to load Hourly Table Work Log link'
      ),
      providesTags: [{ type: 'Summary', id: 'hourlytable-worklog-link' }],
    }),

    getClientsRelationStatsLink: builder.query<string, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetClientsRelationStatisticLink`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<string>(
        'Failed to load Clients Relation Stats link'
      ),
      providesTags: [{ type: 'Summary', id: 'clients-relation-stats-link' }],
    }),

    getClientsRelationStatsRoute: builder.query<DetailRoute, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetClientsRelationStatisticRoute`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<DetailRoute>(
        'Failed to load Clients Relation Stats route'
      ),
      providesTags: [{ type: 'Summary', id: 'clients-relation-stats-route' }],
    }),

    getDateRangeMode: builder.query<string, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetDateRangeMode`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<string>('Failed to load Date Range Mode'),
      providesTags: [{ type: 'Summary', id: 'date-range-mode' }],
    }),

    getDashboardIdByDateRangeMode: builder.query<number, IDateRangeModeDashboardParams>({
      query: ({ dashboardId, dateRangeMode }) => ({
        url: `${SUMMARY_API}/GetDashboardIdByDateRangeMode`,
        params: { dashboardId, dateRangeMode },
      }),
      transformResponse: createTransformResponse<number>(
        'Failed to resolve dashboard ID by Date Range Mode'
      ),
      providesTags: (_result, _error, arg) => [
        { type: 'Summary', id: `dashboard-by-mode-${arg.dashboardId}-${arg.dateRangeMode}` },
      ],
    }),

    getTitle: builder.query<string, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetTitle`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<string>('Failed to load Title'),
      providesTags: [{ type: 'Summary', id: 'summary-title' }],
    }),

    getTimeTooltip: builder.query<string, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetTimeTooltip`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<string>('Failed to load Time Tooltip'),
      providesTags: [{ type: 'Summary', id: 'time-tooltip' }],
    }),

    getTrackedTimePerWeek: builder.query<
      ISummaryDualCurrencyResponse<ITrackedTime>,
      IDateRangeQueryParams
    >({
      query: ({ start, end, useLocalTime, clientId }) => ({
        url: `${SUMMARY_API}/GetTrackedTimePerWeek`,
        params: {
          start: toIsoWithDateOffset(start, useLocalTime),
          end: toIsoWithDateOffset(end, useLocalTime),
          clientId,
        },
      }),
      transformResponse: createTransformResponse<ISummaryDualCurrencyResponse<ITrackedTime>>(
        'Failed to load Tracked Time Per Week'
      ),
      providesTags: [{ type: 'Summary', id: 'tracked-time-week' }],
    }),

    getTrackedTimePerMonth: builder.query<
      ISummaryDualCurrencyResponse<ITrackedTime>,
      IMonthlyDateRangeQueryParams
    >({
      query: ({ dateRanges, useLocalTime, clientId }) => ({
        url: `${SUMMARY_API}/GetTrackedTimePerMonths`,
        method: 'POST',
        params: { clientId },
        body: dateRanges.map((dr) => ({
          startDate: toIsoWithDateOffset(dr.startDate, useLocalTime),
          endDate: toIsoWithDateOffset(dr.endDate, useLocalTime),
        })),
      }),
      transformResponse: createTransformResponse<ISummaryDualCurrencyResponse<ITrackedTime>>(
        'Failed to load Tracked Time Per Month(s)'
      ),
      providesTags: [{ type: 'Summary', id: 'tracked-time-months' }],
      keepUnusedDataFor: 300,
    }),

    getParentBcId: builder.query<number, number>({
      query: (dashboardId) => ({
        url: `${SUMMARY_API}/GetParentBcId`,
        params: { dashboardId },
      }),
      transformResponse: createTransformResponse<number>('Failed to load Parent BC Id'),
      providesTags: [{ type: 'Summary', id: 'parent-bc-id' }],
    }),

    getSummaryClients: builder.query<ISummaryClientOption[], void>({
      query: () => ({
        url: `${SUMMARY_API}/GetSummaryClients`,
      }),
      transformResponse: createTransformResponse<ISummaryClientOption[]>(
        'Failed to load Summary clients'
      ),
      providesTags: [{ type: 'Summary', id: 'summary-clients' }],
    }),

    getSummaryCurrencies: builder.query<ISummaryCurrencyOption[], void>({
      query: () => ({
        url: `${SUMMARY_API}/GetSummaryCurrencies`,
      }),
      transformResponse: createTransformResponse<ISummaryCurrencyOption[]>(
        'Failed to load Summary currencies'
      ),
      providesTags: [{ type: 'Summary', id: 'summary-currencies' }],
    }),
  }),
});

export const {
  useGetDashboardIdQuery,
  useGetMonthlyClientFinanceEnabledQuery,
  useGetMonthlyClientFinanceSummaryQuery,
  useGetRecordsNumberForTopEmployeesQuery,
  useGetRecordsNumberForTopProjectsQuery,
  useGetTopProjectsQuery,
  useGetTopEmployeesQuery,
  useGetTotalTrackedTimeQuery,
  useGetEmployeeTrackedTimeQuery,
  useGetEmployeeWorkLogLinkQuery,
  useGetProjectWorkLogLinkQuery,
  useGetHourlyTableWorkLogLinkQuery,
  useGetClientsRelationStatsLinkQuery,
  useGetClientsRelationStatsRouteQuery,
  useGetDateRangeModeQuery,
  useGetDashboardIdByDateRangeModeQuery,
  useGetTitleQuery,
  useGetTimeTooltipQuery,
  useGetTrackedTimePerWeekQuery,
  useGetTrackedTimePerMonthQuery,
  useGetParentBcIdQuery,
  useGetSummaryClientsQuery,
  useGetSummaryCurrenciesQuery,
} = summaryApi;

export const {
  endpoints: {
    getDashboardId,
    getMonthlyClientFinanceEnabled,
    getMonthlyClientFinanceSummary,
    getRecordsNumberForTopEmployees,
    getRecordsNumberForTopProjects,
    getTopProjects,
    getTopEmployees,
    getTotalTrackedTime,
    getEmployeeTrackedTime,
    getEmployeeWorkLogLink,
    getProjectWorkLogLink,
    getHourlyTableWorkLogLink,
    getClientsRelationStatsLink,
    getDateRangeMode,
    getDashboardIdByDateRangeMode,
    getTitle,
    getTimeTooltip,
    getTrackedTimePerWeek,
    getTrackedTimePerMonth,
    getParentBcId,
    getSummaryClients,
    getSummaryCurrencies,
  },
  reducerPath,
  reducer,
  middleware,
} = summaryApi;
